<template>
  <router-view/>

  <!-- Modal de inactividad -->
  <InactivityModal
    v-model="showInactivityModal"
    :countdownTime="inactivityCountdownSeconds"
    :inactivityMinutes="inactivityMinutes"
    @continue="handleContinueSession"
    @logout="handleLogoutSession"
    @timeout="handleTimeout"
  />
</template>

<script>
import { watch, onMounted, onUnmounted } from 'vue'
import { useStore } from 'vuex'
import { useRoute, useRouter } from 'vue-router'
import { useI18n } from 'vue-i18n'
import InactivityModal from '@/components/InactivityModal.vue'
import useInactivityDetector from '@/composables/useInactivityDetector'

// Tiempos de sesión configurables desde .env (con valores por defecto seguros)
const SESSION_MAX_MINUTES = parseInt(process.env.VUE_APP_SESSION_MAX_MINUTES) || 540
const SESSION_MAX_MS = SESSION_MAX_MINUTES * 60 * 1000

const KEEP_ALIVE_MINUTES = parseInt(process.env.VUE_APP_KEEP_ALIVE_MINUTES) || 50
const KEEP_ALIVE_MS = KEEP_ALIVE_MINUTES * 60 * 1000

const INACTIVITY_MINUTES = parseInt(process.env.VUE_APP_INACTIVITY_MINUTES) || 55
const INACTIVITY_MS = INACTIVITY_MINUTES * 60 * 1000

const INACTIVITY_COUNTDOWN_SECONDS = parseInt(process.env.VUE_APP_INACTIVITY_COUNTDOWN_SECONDS) || 60

const PUBLIC_ROUTES = [
  'auth-login',
  'response-sso',
  'auth-password-recovery',
  'auth-questions',
  'auth-password-confirmation',
  'auth-2fa-channel',
  'auth-2fa-code',
  'auth-invitation-accept'
]

export default {
  name: 'App',
  components: {
    InactivityModal
  },
  setup() {
    const store = useStore()
    const route = useRoute()
    const router = useRouter()
    const { t } = useI18n()

    // ── Timer de inactividad (configurable vía VUE_APP_INACTIVITY_MINUTES) ──────
    const {
      showModal: showInactivityModal,
      resetTimer,
      stopTimer,
      startTimer,
      closeModal
    } = useInactivityDetector(INACTIVITY_MS)

    // ── Keep-alive: refresca el token cada VUE_APP_KEEP_ALIVE_MINUTES mientras el usuario esté activo ─
    let keepAliveInterval = null

    const stopKeepAlive = () => {
      clearInterval(keepAliveInterval)
      keepAliveInterval = null
    }

    const startKeepAlive = () => {
      if (keepAliveInterval) return // ya corriendo, no reiniciar

      keepAliveInterval = setInterval(async () => {
        const token = sessionStorage.getItem('token')
        if (!token) { stopKeepAlive(); return }

        // Si el modal de inactividad ya está visible, la sesión lleva >55 min sin
        // actividad de UI: el modal maneja el estado, el keep-alive no interfiere
        if (showInactivityModal.value) return

        await store.dispatch('auth/refreshToken')
      }, KEEP_ALIVE_MS)
    }

    // ── Timer absoluto de sesión (9 horas) ────────────────────────────────────
    let absoluteSessionTimer = null

    const stopAbsoluteTimer = () => {
      clearTimeout(absoluteSessionTimer)
      absoluteSessionTimer = null
    }

    const forceSessionExpiry = async () => {
      if (!sessionStorage.getItem('token')) return // ya desconectado

      stopTimer()
      stopAbsoluteTimer()
      closeModal()

      sessionStorage.setItem(
        '__session_expired_msg',
        t('inactivity.session-max-reached', { hours: Math.round(SESSION_MAX_MINUTES / 60) })
      )

      const { error } = await store.dispatch('auth/logout')
      if (error) {
        await store.dispatch('auth/clearAuthToken')
      }

      router.push({ name: 'auth-login' })
    }

    const startAbsoluteTimer = () => {
      if (absoluteSessionTimer) return // ya corriendo, no reiniciar

      const dateStr = sessionStorage.getItem('date')
      if (!dateStr) return

      // El backend puede enviar ISO string ("2024-01-15T10:30:00") o timestamp Unix (segundos)
      let loginTime = new Date(dateStr).getTime()
      if (isNaN(loginTime)) {
        loginTime = new Date(Number(dateStr) * 1000).getTime()
      }
      if (isNaN(loginTime)) return

      const remaining = loginTime + SESSION_MAX_MS - Date.now()

      if (remaining <= 0) {
        forceSessionExpiry()
        return
      }

      absoluteSessionTimer = setTimeout(forceSessionExpiry, remaining)
    }

    // ── Handlers del modal de inactividad ────────────────────────────────────
    const handleContinueSession = () => {
      closeModal()
      resetTimer()
      // Reiniciar el keep-alive desde cero: el refreshToken acaba de llamarse,
      // el backend reseteó su reloj, así que la próxima llamada es en 50 min
      stopKeepAlive()
      startKeepAlive()
      // El timer absoluto NO se reinicia: el límite de 9h no se extiende con actividad
    }

    const handleLogoutSession = () => {
      stopTimer()
      stopAbsoluteTimer()
      stopKeepAlive()
    }

    const handleTimeout = () => {
      stopTimer()
      stopAbsoluteTimer()
      stopKeepAlive()
    }

    // ── Watcher de ruta: controla ciclo de vida de los tres timers ────────────
    watch(() => route.name, (newRouteName) => {
      const token = store.getters['auth/getToken']

      if (PUBLIC_ROUTES.includes(newRouteName)) {
        stopTimer()
        stopAbsoluteTimer()
        stopKeepAlive()
      } else if (token) {
        startTimer()
        startAbsoluteTimer()
        startKeepAlive()
      } else {
        stopTimer()
        stopAbsoluteTimer()
        stopKeepAlive()
      }
    }, { immediate: true })

    // ── Cierre de pestaña: invalidar sesión en backend (req. c) ──────────────
    // NOTA: Se comenta esta funcionalidad porque el evento pagehide también se dispara
    // al refrescar la página, lo que provoca cierres de sesión no deseados.
    /*
    const handlePageHide = (event) => {
      if (event.persisted) return

      const token = sessionStorage.getItem('token')
      const host = sessionStorage.getItem('host')
      if (!token || !host) return

      const protocol = process.env.VUE_APP_PROTOCOL || 'https'

      fetch(`${protocol}://${host}/authentication/api/v1/logout`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        keepalive: true
      }).catch(() => {})
    }
    */

    onMounted(() => {
      // window.addEventListener('pagehide', handlePageHide)
    })

    onUnmounted(() => {
      // window.removeEventListener('pagehide', handlePageHide)
      stopAbsoluteTimer()
      stopKeepAlive()
    })

    return {
      showInactivityModal,
      inactivityMinutes: INACTIVITY_MINUTES,
      inactivityCountdownSeconds: INACTIVITY_COUNTDOWN_SECONDS,
      handleContinueSession,
      handleLogoutSession,
      handleTimeout
    }
  }
}
</script>
